/**
 * Utility functions for visitor status and activity checks
 */

// Helper to check if a date is today
export const isToday = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if a visitor/person is currently active (shown as "day visitor" in operational view)
 * A visitor is active when they have an active role and activity today
 */
export const isVisitorActiveToday = (person: any): boolean => {
  if (!person) return false;

  // Get the ACTIVE authorized visitor or service_provider role
  // Exclude is_authorized=false roles (pre-registrations for future visits)
  const visitorRole = person.roles?.find((r: any) =>
    (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
    r.is_active === true && r.is_authorized === true
  );

  if (!visitorRole) return false;

  const metadata = visitorRole.role_metadata || {};

  // Pre-registered visitors are future visits — never count as active today
  if (metadata.visitor_state === 'pre_registered') return false;

  // Check if visitor was active today (same logic as useOperationalVisitors)
  const firstEntry = metadata.first_entry_at;
  const lastEntry = metadata.last_entry_at;
  const createdAt = visitorRole.created_at;

  return (
    isToday(firstEntry) ||
    isToday(lastEntry) ||
    isToday(createdAt) ||
    // Also include visitors awaiting entry with valid window today
    (metadata.visitor_state === 'awaiting_entry' &&
     metadata.entry_window_expires_at &&
     isToday(metadata.entry_window_expires_at))
  );
};

/**
 * Get visitor status for display
 */
export const getVisitorStatus = (person: any): {
  isActive: boolean;
  status: 'inside' | 'awaiting' | 'exited' | 'inactive' | 'revoked';
  statusLabel: string;
} => {
  const isActive = isVisitorActiveToday(person);

  if (!isActive) {
    return {
      isActive: false,
      status: 'inactive',
      statusLabel: 'Inativo',
    };
  }

  // Get active role metadata
  const visitorRole = person.roles?.find((r: any) =>
    (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
    r.is_active === true
  );
  const metadata = visitorRole?.role_metadata || {};
  const state = metadata.visitor_state;

  switch (state) {
    case 'inside':
      return {
        isActive: true,
        status: 'inside',
        statusLabel: 'Dentro',
      };
    case 'awaiting_entry':
      return {
        isActive: true,
        status: 'awaiting',
        statusLabel: 'Aguardando Entrada',
      };
    case 'exited':
    case 'expired':
    case 'consumed':
      return {
        isActive: true,
        status: 'exited',
        statusLabel: 'Saiu',
      };
    case 'revoked':
      return {
        isActive: true,
        status: 'revoked',
        statusLabel: 'Revogado',
      };
    case 'pre_registered':
      return {
        isActive: false,
        status: 'inactive',
        statusLabel: 'Pré-Cadastrado',
      };
    default:
      return {
        isActive: true,
        status: 'awaiting',
        statusLabel: 'Aguardando',
      };
  }
};

/**
 * Format relative time from a date (e.g., "há 2h", "há 30min")
 */
export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `há ${diffDays}d`;
  } else if (diffHours > 0) {
    return `há ${diffHours}h`;
  } else if (diffMinutes > 0) {
    return `há ${diffMinutes}min`;
  } else {
    return 'agora';
  }
};

/**
 * Format date header for grouping (e.g., "HOJE - 22 Jan 2026", "ONTEM - 21 Jan 2026")
 */
export const formatDateHeader = (dateStr: string): string => {
  // Parse pt-BR date format (DD/MM/YYYY)
  const parts = dateStr.split('/');
  if (parts.length !== 3) {
    return dateStr; // Return as-is if not in expected format
  }

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  const year = parseInt(parts[2], 10);

  const date = new Date(year, month, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);

  const isDateToday = dateOnly.getTime() === today.getTime();
  const isDateYesterday = dateOnly.getTime() === yesterday.getTime();

  const formattedDate = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  if (isDateToday) {
    return `HOJE - ${formattedDate}`;
  } else if (isDateYesterday) {
    return `ONTEM - ${formattedDate}`;
  } else {
    return formattedDate.toUpperCase();
  }
};

/**
 * Calculate duration between two dates
 */
export const calculateDuration = (startStr: string | null | undefined, endStr?: string | null): string => {
  if (!startStr) return '-';

  const start = new Date(startStr);
  const end = endStr ? new Date(endStr) : new Date();
  const diffMs = end.getTime() - start.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  } else {
    return `${minutes}min`;
  }
};

/**
 * Group visitors by date
 */
export const groupByDate = (visitors: any[]): Record<string, any[]> => {
  return visitors.reduce((acc, visitor) => {
    const entryTime = visitor.first_entry_at || visitor.last_entry_at || visitor.created_at;
    if (!entryTime) return acc;

    const date = new Date(entryTime);
    const dateKey = date.toLocaleDateString('pt-BR');

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(visitor);

    return acc;
  }, {} as Record<string, any[]>);
};
